import React, { useCallback, useState } from "react";
import { Modal, Space, Spin } from "antd";
import Dragger from "antd/lib/upload/Dragger";
import * as xlsx from "xlsx";
import { bulkPrepareMeters, retrieveBulkProcessResults, uploadDataToS3BucketByUrl } from "utils/api";
import { defaultNgiErrorMethod, errorMessage, successMessage } from "components/Common/responses/message";
import uploadArea from "assets/images/svg/uploadArea.svg";
import CustomButton from "components/Common/buttons/CustomButton";
import closeIcon from "assets/images/svg/closeSmall.svg";
import bulkRemoveMeterIcon from "assets/images/svg/bulkRemoveMeter.svg";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { generateCSV } from "utils/helpers/functions";
import CustomLink from "components/Common/buttons/CustomLink";
import { CSV_TEMPLATES } from "utils/constants";
import { getFormattedResult } from "pages/meter-management/configuration/meters/utils";

const useStyles = makeStyles({
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 15,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    padding: "5px 10px",
    marginTop: 10,
  },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  errorsContainer: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 15,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    padding: 10,
    marginBottom: 20,
    backgroundColor: "#ffc0cb21",
  },
  errorDesc: {
    marginBottom: 10,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  errorItem: {
    width: "100%",
  },
  descriptionTitle: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  withIcon: {
    display: "flex",
    alignItems: "center",

    "&>img": {
      marginRight: 10,
    },
  },
});
const BulkMeterValidationModal = ({ onClose }) => {
  const classes = useStyles();
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorList, setErrorList] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadData, setUploadData] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const readUploadFile = useCallback(e => {
    setDataLoading(true);
    const file = e.file;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet, { raw: false });
        const csv = xlsx.utils.sheet_to_csv(worksheet, { raw: false });

        const formattedResult = json.map(row => ({ ...row }));
        const noError = formattedResult.every(item => item.meterBarcode || null);
        if (!noError) {
          errorMessage("File should contain a list of meter barcodes");
          setDataLoading(false);
          return;
        }
        setError(!noError);

        setFile(file);
        setUploadData(csv);
        setDataLoading(false);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const onRetrieveBulkProcessResults = useCallback(async id => {
    setTimeout(async () => {
      await retrieveBulkProcessResults("", id).then(async res => {
        if (!!res?.data?.result?.completedAt) {
          openConfirmationModal();
          successMessage(`Meters were uploaded successfully, ${getFormattedResult(res?.data?.result)}`, 100);
          setDataLoading(false);
        } else {
          await onRetrieveBulkProcessResults(id);
        }
      });
    }, 3000);
  }, []);

  const onSubmit = useCallback(async () => {
    setDataLoading(true);
    bulkPrepareMeters()
      .then(res => {
        const {
          result: { url, id },
        } = res?.data;
        uploadDataToS3BucketByUrl(url, uploadData).then(async () => {
          await onRetrieveBulkProcessResults(id);
        });
      })
      .catch(err => {
        setDataLoading(false);
        defaultNgiErrorMethod(err);
      });
  }, [uploadData]);

  const removeFile = useCallback(() => {
    setFile(null);
    setUploadData(null);
    setErrorList(null);
    setError(false);
  }, []);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => {
    setIsConfirmationOpen(false);
    onClose();
  }, []);

  return (
    <Modal
      title={
        <div className={classes.withIcon}>
          <img src={bulkRemoveMeterIcon} alt="" />
          <div className={classes.descriptionTitle}>
            Bulk Meter Validation
            <span>Please upload a file with all meters you want to be validated</span>
          </div>
        </div>
      }
      open
      onCancel={dataLoading ? openConfirmationModal : onClose}
      footer={[]}
      closable={false}
      onOk={onSubmit}
    >
      <div>
        <Spin spinning={dataLoading}>
          <Space>
            <CustomLink color="#0085FF" onClick={() => generateCSV(CSV_TEMPLATES.BULK_METER_VALIDATE)}>
              Download *.csv template
            </CustomLink>
          </Space>

          {file ? (
            <div className={classes.fileContainer}>
              <div className={classes.fileTitle}>{`${file.name}`}</div>
              <div onClick={removeFile} className={classes.removeBtn}>
                <img src={closeIcon} alt="remove" />
              </div>
            </div>
          ) : (
            <Dragger accept=".csv" showUploadList={false} customRequest={readUploadFile} name="file">
              <img className="upload-bg" src={uploadArea} alt="upload" />
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">
                Support for a single upload. Strictly prohibit from uploading company data or other band files
              </p>
            </Dragger>
          )}
          <br />
          {errorList && (
            <div className={classes.errorsContainer}>
              <div className={classes.errorDesc}>{errorList?.desc}</div>
              <ul>
                {errorList?.items?.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          <Space>
            <CustomButton onClick={onClose} type="primary" size="small" color="outlined" text="Cancel" />

            <CustomButton
              onClick={onSubmit}
              disabled={error || !file}
              htmlType="submit"
              type="primary"
              text="Confirm"
              size="small"
            />
          </Space>
        </Spin>
      </div>
      {isConfirmationOpen && (
        <ConfirmationModal
          hideCancel
          description="The validation has already started. Closing the popup doesn’t stop the process! We will send you an email when the process is completed!"
          onCancel={closeConfirmationModal}
          onConfirm={closeConfirmationModal}
          confirmBtnTitle="Ok"
          cancelBtnTitle="Close"
        />
      )}
    </Modal>
  );
};

export default BulkMeterValidationModal;
